import { useState, useEffect, useCallback } from 'react';
import { getStorageItem } from '@/utils/storage/local-storage';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectProjectSettings, selectWorkingProjectCanonicalTimezone } from '@/store/slices/project/selectors';
import { saveWorkingProjectByFields } from '@/store/slices/project';

const TIMEZONE_KEY = 'last-timezone';
const TIMEZONE_PROJECT_KEY = 'last-timezone-project';

// important changes from BDEV-5455:
// 1. We will no longer store timezones in local storage, but we will pull from it for backwards-compat
// 2. The backwards compat does not have to live for very long - after November 2026 the existing keys will
//    have expired and the fallback to local storage should be removed
// TODO: remove local storage fallback if after November 2026
const readStoredTimezone = (projectUuid: string | undefined): string | undefined => {
  if (!projectUuid) return undefined;

  try {
    const storedProjectUuid = getStorageItem(TIMEZONE_PROJECT_KEY);

    if (storedProjectUuid === projectUuid) {
      return getStorageItem(TIMEZONE_KEY) ?? undefined;
    }
  } catch (error) {
    console.error('Failed to read timezone from storage:', error);
  }

  return undefined;
};

const useCanonicalTimezone = (projectUuid: string | undefined): [string | undefined, (tz: string) => void] => {
  const canonicalTimezone = useAppSelector(selectWorkingProjectCanonicalTimezone);
  const workingProjectSettings = useAppSelector(selectProjectSettings);
  const dispatch = useAppDispatch();

  const [timezone, setTimezoneState] = useState<string | undefined>(
    () => canonicalTimezone ?? readStoredTimezone(projectUuid)
  );

  useEffect(() => {
    if (canonicalTimezone) {
      setTimezoneState(canonicalTimezone);
    } else {
      setTimezoneState(readStoredTimezone(projectUuid));
    }
  }, [canonicalTimezone, projectUuid]);

  const setTimezone = useCallback(
    (tz: string) => {
      if (!workingProjectSettings) return;

      // will cause setTimezoneState to be set in the effect
      dispatch(
        saveWorkingProjectByFields({
          settings: {
            ...workingProjectSettings,
            canonicalTimezone: tz,
          },
        })
      );
    },
    [dispatch, workingProjectSettings]
  );

  return [timezone, setTimezone];
};

export default useCanonicalTimezone;
